import FindMeetingTimeSlot from 'owa-calendar-scheduling-service/lib/schema/FindMeetingTimeSlot';
import PlacesSource from 'owa-service/lib/contract/PlacesSource';
import TimeConstraint from 'owa-calendar-scheduling-service/lib/schema/TimeConstraint';
import TimeSlot from 'owa-service/lib/contract/TimeSlot';
import { addMinutes } from 'owa-datetime/lib/owaDate';
import { formatDate } from 'owa-datetime';
import { utcDate } from 'owa-datetime';
import RecurrenceType from 'owa-service/lib/contract/RecurrenceType';
import RecurrenceConstraint, {
    RecurrenceConstraintPattern,
    RecurrenceConstraintRange,
    RecurrencePatternType,
    RecurrenceRangeType,
} from 'owa-calendar-scheduling-service/lib/schema/RecurrenceConstraint';
import IntervalRecurrencePatternBaseType from 'owa-service/lib/contract/IntervalRecurrencePatternBaseType';
import {
    isDailyRecurrence,
    isWeeklyRecurrence,
    isRelativeMonthlyRecurrence,
    isRelativeYearlyRecurrence,
    isAbsoluteMonthlyRecurrence,
    isAbsoluteYearlyRecurrence,
} from 'owa-recurrence-utils/lib/checkRecurrenceTypes';
import EndDateRecurrence from 'owa-service/lib/contract/EndDateRecurrence';
import NumberedRecurrence from 'owa-service/lib/contract/NumberedRecurrence';
import RecurrenceRangeBaseType from 'owa-service/lib/contract/RecurrenceRangeBaseType';
import WeeklyRecurrence from 'owa-service/lib/contract/WeeklyRecurrence';
import AbsoluteMonthlyRecurrence from 'owa-service/lib/contract/AbsoluteMonthlyRecurrence';
import RelativeYearlyRecurrence from 'owa-service/lib/contract/RelativeYearlyRecurrence';
import AbsoluteYearlyRecurrence from 'owa-service/lib/contract/AbsoluteYearlyRecurrence';
import RelativeMonthlyRecurrence from 'owa-service/lib/contract/RelativeMonthlyRecurrence';
import { DayOfWeek } from 'office-ui-fabric-react/lib/utilities/dateValues/DateValues';
import { isFeatureEnabled } from 'owa-feature-flags';
const DATE_FORMAT = 'yyyy-MM-dd HH:mm:ss';
import { MONTHS_OF_YEAR } from 'owa-recurrence-utils/lib/constants';
import MonthNamesType from 'owa-timeformat/lib/store/schema/MonthNamesType';

/**
 * Create LocationConstraint for FML request
 */
export default function createTimeConstraints(
    source: PlacesSource,
    meetingTimeSlot: TimeSlot,
    recurrence: RecurrenceType
): TimeConstraint {
    let timeslots = [];

    if ((source & PlacesSource.Relevance) == PlacesSource.Relevance && meetingTimeSlot != null) {
        timeslots = [];
        let start = utcDate(meetingTimeSlot.StartTime);
        let end = addMinutes(start, meetingTimeSlot.DurationInMinutes);
        timeslots.push({
            Start: {
                DateTime: formatDate(start, DATE_FORMAT),
                TimeZone: start.tz,
            },
            End: {
                DateTime: formatDate(end, DATE_FORMAT),
                TimeZone: end.tz,
            },
        } as FindMeetingTimeSlot);

        return {
            timeslots: timeslots,
            recurrence: isFeatureEnabled('cal-mf-availabilityForRecurrence')
                ? createRecurrenceConstraints(recurrence, start.tz)
                : null,
        } as TimeConstraint;
    }

    return null;
}

function createRecurrenceConstraints(
    recurrence: RecurrenceType,
    timezone: string
): RecurrenceConstraint {
    let recurrenceConstraint: RecurrenceConstraint = null;
    if (recurrence) {
        recurrenceConstraint = {
            pattern: createRecurrencePattern(recurrence),
            range: createRecurrenceRange(recurrence, timezone),
        };
    }

    return recurrenceConstraint;
}

function createRecurrencePattern(recurrence: RecurrenceType): RecurrenceConstraintPattern {
    let recurrenceInterval = (recurrence.RecurrencePattern as IntervalRecurrencePatternBaseType)
        .Interval;

    if (!recurrenceInterval) {
        recurrenceInterval = 1;
    }

    const type = getRecurrenceType(recurrence);

    if (type == 'daily') {
        return {
            type: type,
            interval: recurrenceInterval,
        } as RecurrenceConstraintPattern;
    }

    if (type == 'weekly') {
        const weeklyRecurrence = recurrence.RecurrencePattern as WeeklyRecurrence;

        return {
            type: type,
            interval: recurrenceInterval,
            firstDayofWeek: weeklyRecurrence.FirstDayOfWeek,
            daysOfWeek: getDaysOfWeek(weeklyRecurrence.DaysOfWeek),
        };
    }

    if (type == 'absoluteMonthly') {
        const absoluteMonthlyRecurrence = recurrence.RecurrencePattern as AbsoluteMonthlyRecurrence;
        return {
            type: type,
            interval: recurrenceInterval,
            dayOfMonth: absoluteMonthlyRecurrence.DayOfMonth,
        };
    }

    if (type == 'relativeMonthly') {
        const relativeMonthlyRecurrence = recurrence.RecurrencePattern as RelativeMonthlyRecurrence;
        return {
            type: type,
            interval: recurrenceInterval,
            weekIndex: relativeMonthlyRecurrence.DayOfWeekIndex,
            daysOfWeek: getDaysOfWeek(relativeMonthlyRecurrence.DaysOfWeek),
        };
    }

    if (type == 'absoluteYearly') {
        const absoluteYearlyRecurrence = recurrence.RecurrencePattern as AbsoluteYearlyRecurrence;
        return {
            type: type,
            interval: recurrenceInterval,
            month: MONTHS_OF_YEAR.indexOf(absoluteYearlyRecurrence.Month as MonthNamesType) + 1,
            dayOfMonth: absoluteYearlyRecurrence.DayOfMonth,
        };
    }

    if (type == 'relativeYearly') {
        const relativeYearlyRecurrence = recurrence.RecurrencePattern as RelativeYearlyRecurrence;
        return {
            type: type,
            interval: recurrenceInterval,
            month: MONTHS_OF_YEAR.indexOf(relativeYearlyRecurrence.Month as MonthNamesType) + 1,
            weekIndex: relativeYearlyRecurrence.DayOfWeekIndex,
            daysOfWeek: getDaysOfWeek(relativeYearlyRecurrence.DaysOfWeek),
        };
    }

    return null;
}

function createRecurrenceRange(
    recurrence: RecurrenceType,
    timezone: string
): RecurrenceConstraintRange {
    const range = recurrence.RecurrenceRange;
    const rangeType = getRecurrenceRangeType(range);

    const endDateString = (<EndDateRecurrence>range).EndDate;
    const numberOfOccurences = (<NumberedRecurrence>range).NumberOfOccurrences;
    return {
        type: rangeType,
        startDate: recurrence.RecurrenceRange.StartDate,
        endDate: endDateString,
        numberOfOccurrences: numberOfOccurences,
        recurrenceTimeZone: timezone,
    } as RecurrenceConstraintRange;
}

function getRecurrenceType(recurrence: RecurrenceType): RecurrencePatternType {
    if (isDailyRecurrence(recurrence)) {
        return 'daily';
    } else if (isWeeklyRecurrence(recurrence)) {
        return 'weekly';
    } else if (isRelativeMonthlyRecurrence(recurrence)) {
        return 'relativeMonthly';
    } else if (isRelativeYearlyRecurrence(recurrence)) {
        return 'absoluteYearly';
    } else if (isAbsoluteMonthlyRecurrence(recurrence)) {
        return 'absoluteMonthly';
    } else if (isAbsoluteYearlyRecurrence(recurrence)) {
        return 'absoluteYearly';
    }
    throw new Error('');
}

function getRecurrenceRangeType(range: RecurrenceRangeBaseType): RecurrenceRangeType {
    if ((<NumberedRecurrence>range).NumberOfOccurrences) {
        return 'numbered';
    } else if ((<EndDateRecurrence>range).EndDate) {
        return 'endDate';
    } else {
        return 'noEnd';
    }
}

function getDaysOfWeek(daysOfWeekString: string): DayOfWeek[] {
    let recurrenceDays = daysOfWeekString.split(' ');
    return recurrenceDays.map(x => DayOfWeek[x]);
}
